import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Gem, ShieldCheck, HeartHandshake, CalendarCheck, HandCoins, Search, Phone, Star, CarFront, Handshake } from "lucide-react";
import { api } from "@/lib/api";
import { COMPANY, COPY, DEALER, FEATURES, waLink } from "@/lib/company";
import { WaIcon } from "@/lib/whatsapp";
import { usePageMeta } from "@/lib/seo";
import VehicleCard from "@/components/VehicleCard";
import LocationMap from "@/components/LocationMap";
import HeroVideo from "@/components/HeroVideo";

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const PILLARS = [
  { icon: Gem, title: "Persönlich ausgewählt", text: "Sportwagen und Premiumfahrzeuge, die wir selbst gerne fahren würden – bewusst ausgesucht statt Masse." },
  { icon: ShieldCheck, title: "Ehrliche Angaben", text: "Alle Daten und Fotos stammen direkt aus unseren Inseraten – nachvollziehbar, vollständig und aktuell." },
  { icon: HeartHandshake, title: "Direkter Kontakt", text: "Sie sprechen direkt mit uns – per WhatsApp, Telefon oder E-Mail. Kurze Wege, schnelle Antworten." },
  { icon: CalendarCheck, title: "Termin nach Wunsch", text: "Besichtigung und Probefahrt in Münchendorf – auch am Wochenende nach persönlicher Vereinbarung." },
];

const CARDS = [
  { icon: CarFront, title: "Fahrzeuge mit Charakter", text: "Entdecken Sie Sportwagen und Premiumfahrzeuge mit ausführlichen Informationen und hochwertigen Bildergalerien." },
  { icon: WaIcon, title: "Ein direkter Draht", text: "Fragen zu Ausstattung, Preis oder Verfügbarkeit? Kontaktieren Sie uns unkompliziert und direkt über WhatsApp." },
  { icon: Handshake, title: "Persönlich statt anonym", text: "Alles Weitere besprechen wir persönlich – individuell, unkompliziert und ohne automatische Terminbuchung." },
];

const Eyebrow = ({ children }) => <p className="eyebrow mb-4">{children}</p>;

const SectionHead = ({ eyebrow, title, link }) => (
  <motion.div {...reveal} className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display font-bold text-3xl md:text-4xl text-ink">{title}</h2>
    </div>
    {link}
  </motion.div>
);

const Grid = ({ vehicles, testId, cols = "lg:grid-cols-3" }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-6 md:gap-8`} data-testid={testId}>
    {vehicles.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
  </div>
);

const ProcessCard = ({ icon: Icon, title, text, i }) => (
  <motion.div {...reveal} transition={{ ...reveal.transition, delay: i * 0.12 }} data-testid={`home-process-card-${i}`}
    className="group flex gap-5 items-start rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-7 transition-[background-color,border-color] duration-300 hover:bg-white/[0.07] hover:border-white/20">
    <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white text-brand shrink-0 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
      <Icon size={20} strokeWidth={1.7} />
    </span>
    <div>
      <p className="font-display font-bold text-lg text-white">{title}</p>
      <p className="text-sm text-white/55 mt-1.5 leading-relaxed">{text}</p>
    </div>
  </motion.div>
);

const Process = () => (
  <section className="relative bg-deep text-white overflow-hidden" data-testid="home-process-section">
    <div aria-hidden="true" className="pointer-events-none absolute -right-48 top-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-brand/10 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <motion.div {...reveal}>
        <p className="eyebrow mb-6 !text-white/50">Fahrleidenschaft trifft Persönlichkeit</p>
        <h2 className="font-display font-bold text-3xl md:text-5xl leading-tight" data-testid="home-process-title">
          Besondere Fahrzeuge.<br /><span className="text-white/50">Persönlicher Kontakt.</span>
        </h2>
        <p className="text-white/60 mt-6 leading-relaxed max-w-md">
          Entdecken Sie unsere Auswahl an Sport- und Premiumfahrzeugen. Bei {COMPANY.name} stehen Ihre Wünsche im Mittelpunkt –
          mit direktem Kontakt, persönlicher Beratung und echter Begeisterung für Automobile.
        </p>
        <Link to="/fahrzeuge" data-testid="home-process-cta"
          className="btn-pill inline-flex items-center gap-2 bg-white text-black font-semibold px-8 py-4 text-sm mt-10 hover:bg-white/90">
          Verfügbare Fahrzeuge <ArrowRight size={16} />
        </Link>
      </motion.div>
      <div className="glass-dark rounded-3xl p-3 md:p-4 space-y-3">
        {CARDS.map((c, i) => <ProcessCard key={c.title} {...c} i={i} />)}
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
    <motion.div {...reveal} className="mb-12">
      <Eyebrow>Warum {COMPANY.name}?</Eyebrow>
      <h2 className="font-display font-bold text-3xl md:text-4xl max-w-xl text-ink">{COPY.tagline}</h2>
    </motion.div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 bg-surface card-soft rounded-3xl overflow-hidden border border-line" data-testid="home-pillars">
      {PILLARS.map(({ icon: Icon, title, text }, i) => (
        <motion.div key={title} {...reveal}
          className={`p-8 md:p-10 ${i < 3 ? "lg:border-r" : ""} ${i % 2 === 0 ? "sm:border-r" : ""} border-b lg:border-b-0 border-line`}>
          <Icon size={26} strokeWidth={1.5} className="mb-5 text-ink" />
          <h3 className="font-display font-bold text-lg mb-3 text-ink">{title}</h3>
          <p className="text-sm text-dim leading-relaxed">{text}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const FindetTeaser = ({ wide }) => (
  <motion.div {...reveal} className={`bg-surface card-soft border border-line rounded-3xl p-10 md:p-12 flex ${wide ? "flex-col md:flex-row md:items-center md:justify-between gap-8" : "flex-col items-start"}`}>
    <div>
      <Search size={28} strokeWidth={1.5} className="mb-6 text-ink" />
      <h3 className="font-display font-bold text-2xl md:text-3xl mb-4 text-ink">Fahrzeugsuche</h3>
      <p className={`text-sm text-dim leading-relaxed max-w-sm ${wide ? "" : "mb-8"}`}>
        Ihr Wunschfahrzeug ist nicht im Bestand? Beschreiben Sie es uns – wir halten für Sie gezielt Ausschau.
      </p>
    </div>
    <Link to="/fahrzeugsuche" data-testid="home-findet-cta" className={`btn-pill inline-flex items-center gap-2 bg-action text-action-ink font-semibold px-7 py-3.5 text-sm hover:bg-action-hover shrink-0 ${wide ? "self-start md:self-auto" : "mt-auto"}`}>
      Suchauftrag starten <ArrowRight size={15} />
    </Link>
  </motion.div>
);

const Teasers = () => (FEATURES.findet || FEATURES.ankauf) && (
  <section className={`max-w-7xl mx-auto px-5 sm:px-8 pb-20 md:pb-28 grid gap-6 ${FEATURES.ankauf && FEATURES.findet ? "md:grid-cols-2" : ""}`}>
    {FEATURES.findet && <FindetTeaser wide={!FEATURES.ankauf} />}
    {FEATURES.ankauf && (
    <motion.div {...reveal} className="bg-deep text-white rounded-3xl p-10 md:p-12 flex flex-col items-start">
      <HandCoins size={28} strokeWidth={1.5} className="mb-6" />
      <h3 className="font-display font-bold text-2xl md:text-3xl mb-4">Fahrzeugankauf</h3>
      <p className="text-sm text-white/60 leading-relaxed mb-8 max-w-sm">
        Verkaufen Sie Ihr Fahrzeug schnell und fair. In wenigen Schritten zur kostenlosen Bewertung – wir melden uns umgehend.
      </p>
      <Link to="/ankauf" data-testid="home-ankauf-cta" className="btn-pill mt-auto inline-flex items-center gap-2 bg-white text-black font-semibold px-7 py-3.5 text-sm hover:bg-white/90">
        Fahrzeug verkaufen <ArrowRight size={15} />
      </Link>
    </motion.div>
    )}
  </section>
);

const Reviews = ({ data }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 md:pb-28" data-testid="reviews-section">
    <SectionHead eyebrow="Google Bewertungen" title="Was unsere Kunden sagen"
      link={data.rating && <p className="font-display font-bold text-3xl text-ink">{data.rating.toLocaleString("de-AT")}<Star size={18} className="inline ml-1 text-amber-500" fill="currentColor" /></p>} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {data.reviews.slice(0, 4).map((r, i) => (
        <div key={`${r.name}-${i}`} className="bg-surface card-soft rounded-2xl p-7">
          <p className="text-sm text-dim leading-relaxed line-clamp-6">„{r.text}"</p>
          <p className="text-sm font-semibold text-ink mt-6">{r.name}</p>
          <p className="text-xs text-faint">{r.time}</p>
        </div>
      ))}
    </div>
  </section>
);

const Visit = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 md:pb-32 space-y-6">
    <motion.div {...reveal} data-testid="home-visit-card"
      className="bg-surface card-soft border border-line rounded-2xl p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-6 text-center md:text-left">
      <div>
        <h2 className="font-display font-bold text-2xl md:text-3xl mb-2 text-ink">Besuchen Sie uns in unserem Showroom</h2>
        <p className="text-dim text-sm leading-relaxed max-w-lg mx-auto md:mx-0">
          {COMPANY.street}, {COMPANY.city}. Besichtigung und Probefahrt nach Terminvereinbarung – wir nehmen uns Zeit für Sie.
        </p>
      </div>
      <div className="flex flex-wrap justify-center md:justify-end gap-3 shrink-0">
        <a href={waLink("Hallo AD Motors, ich würde gerne einen Besichtigungstermin vereinbaren.")} target="_blank" rel="noopener noreferrer"
          data-testid="home-whatsapp-cta" className="btn-pill inline-flex items-center gap-2 bg-[#25D366] text-[#06210f] font-semibold px-6 py-3 text-sm hover:brightness-110">
          <WaIcon size={16} /> WhatsApp schreiben
        </a>
        <a href={COMPANY.phoneLink} data-testid="home-call-cta"
          className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink font-semibold px-6 py-3 text-sm hover:bg-action-hover">
          <Phone size={16} /> {COMPANY.phone}
        </a>
      </div>
    </motion.div>
    <motion.div {...reveal}><LocationMap testId="home-map" /></motion.div>
  </section>
);

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [reviews, setReviews] = useState(null);
  usePageMeta(null, COPY.seoDescription);

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {}).finally(() => setLoaded(true));
    if (!DEALER.googleReviews) api.get("/reviews").then(({ data }) => data.source === "google" && data.reviews?.length && setReviews(data)).catch(() => {});
  }, []);

  const allLink = (
    <Link to="/fahrzeuge" data-testid="home-all-vehicles-link" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-brand transition-colors">
      Alle Fahrzeuge <ArrowRight size={15} />
    </Link>
  );

  return (
    <div data-testid="home-page">
      <HeroVideo />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <SectionHead eyebrow="Unser Bestand" title="Ausgewählte Fahrzeuge" link={allLink} />
        <Grid vehicles={vehicles.slice(0, 6)} testId="home-vehicles-grid" />
        {loaded && vehicles.length === 0 && (
          <p className="text-dim text-sm py-10" data-testid="home-vehicles-empty">Der Bestand wird gerade aktualisiert – schreiben Sie uns gerne direkt.</p>
        )}
        {vehicles.length > 0 && (
          <div className="mt-14 text-center">
            <Link to="/fahrzeuge" data-testid="home-all-vehicles-btn"
              className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink font-semibold px-10 py-4 text-sm hover:bg-action-hover">
              Alle {vehicles.length} Fahrzeuge ansehen <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </section>

      <Process />
      <Pillars />
      <Teasers />
      {!DEALER.googleReviews && reviews && <Reviews data={reviews} />}
      <Visit />
    </div>
  );
}
